import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCheck, Info, Loader2, RotateCcw, Save } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import InnerLayout from "../layouts/inner-layout";
import LoadingSpinner from "../helper-components/loading-spinner";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";
import {
  followUpSettingsApi,
  followUpSettingsUpdateApi,
} from "../../redux/apis/follow-up-api";
import {
  DEFAULT_FOLLOW_UP_MESSAGE,
  FOLLOW_UP_MESSAGE_MAX_LENGTH,
  MESSAGE_PLACEHOLDERS,
  SAMPLE_MESSAGE_VALUES,
  fillTemplate,
} from "../../utils/follow-up";

const CARD_CLASS =
  "flex flex-col rounded-xl border border-[var(--follow-up-border)] bg-[var(--follow-up-surface-bg)]";

const buttonClass = (variant) =>
  `inline-flex h-9 items-center justify-center gap-2 rounded-lg px-4 text-[length:var(--follow-up-text-fs)] font-[var(--follow-up-label-fw)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--follow-up-focus-ring)] disabled:cursor-not-allowed ${
    variant === "primary"
      ? "bg-[var(--follow-up-accent)] text-[var(--follow-up-accent-fg)] hover:bg-[var(--follow-up-accent-hover)] disabled:bg-[var(--follow-up-disabled-bg)] disabled:text-[var(--follow-up-disabled-fg)]"
      : "border border-[var(--follow-up-reopen-btn-border)] bg-[var(--follow-up-reopen-btn-bg)] text-[var(--follow-up-reopen-btn-fg)] hover:bg-[var(--follow-up-reopen-btn-hover-bg)]"
  }`;

// How the message looks in WhatsApp, filled with sample details
const MessagePreview = ({ message }) => (
  <section
    aria-labelledby="follow-up-preview-heading"
    className={`${CARD_CLASS} overflow-hidden`}
    style={{ boxShadow: "var(--follow-up-shadow)" }}
  >
    <h2 id="follow-up-preview-heading" className="sr-only">
      Message preview
    </h2>
    <div className="flex items-center gap-3 bg-[var(--follow-up-preview-header-bg)] px-4 py-3 text-[var(--follow-up-preview-header-fg)]">
      <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--follow-up-whatsapp-bg)]">
        <FaWhatsapp size={18} aria-hidden="true" />
      </span>
      <div>
        <p className="text-[length:var(--follow-up-text-fs)] font-[var(--follow-up-label-fw)]">
          {SAMPLE_MESSAGE_VALUES.customer_name}
        </p>
        <p className="text-[length:var(--follow-up-small-fs)]">Preview with sample details</p>
      </div>
    </div>
    <div className="min-h-[18rem] flex-1 bg-[var(--follow-up-preview-chat-bg)] p-4">
      <div
        className="ml-auto max-w-[90%] rounded-lg rounded-tr-none bg-[var(--follow-up-preview-bubble-bg)] px-3 py-2 text-[var(--follow-up-preview-bubble-fg)]"
        style={{ boxShadow: "var(--follow-up-shadow)" }}
      >
        <p className="whitespace-pre-wrap break-words text-[length:var(--follow-up-text-fs)]">
          {message.trim() ? message : "Your message will show here."}
        </p>
        <p className="mt-1 flex items-center justify-end gap-1 text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-muted-fg)]">
          10:30 AM <CheckCheck size={14} aria-hidden="true" className="text-[var(--follow-up-upcoming-fg)]" />
        </p>
      </div>
    </div>
  </section>
);

const FollowUpMessageSettings = () => {
  const textareaRef = useRef(null);
  const [message, setMessage] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const loadMessage = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const res = await followUpSettingsApi();
      const stored = res?.data?.data?.whatsapp_message || DEFAULT_FOLLOW_UP_MESSAGE;
      setMessage(stored);
      setSavedMessage(stored);
    } catch (error) {
      setLoadError(error?.response?.data?.message ?? "Couldn't load the follow-up message.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessage();
  }, [loadMessage]);

  // puts {key} where the cursor is (or over the selected text)
  const insertPlaceholder = (key) => {
    const token = `{${key}}`;
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? message.length;
    const end = textarea?.selectionEnd ?? message.length;
    const next = `${message.slice(0, start)}${token}${message.slice(end)}`;
    if (next.length > FOLLOW_UP_MESSAGE_MAX_LENGTH) {
      errorToast(`The message can be at most ${FOLLOW_UP_MESSAGE_MAX_LENGTH} characters`);
      return;
    }
    setMessage(next);
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const trimmedMessage = message.trim();
  const canSave = !isSaving && trimmedMessage.length > 0 && message !== savedMessage;

  const saveMessage = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const res = await followUpSettingsUpdateApi({ whatsapp_message: trimmedMessage });
      setMessage(trimmedMessage);
      setSavedMessage(trimmedMessage);
      successToast(res?.data?.message ?? "Follow-up message saved");
    } catch (error) {
      errorToast(error?.response?.data?.message ?? "Couldn't save the follow-up message");
    } finally {
      setIsSaving(false);
    }
  };

  const renderContent = () => {
    if (isLoading) return <LoadingSpinner />;
    if (loadError) {
      return (
        <div className={`${CARD_CLASS} items-center gap-3 p-10 text-center`}>
          <p className="text-[length:var(--follow-up-text-fs)] text-[var(--follow-up-text-fg)]">{loadError}</p>
          <button type="button" onClick={loadMessage} className={buttonClass("primary")}>
            Try again
          </button>
        </div>
      );
    }
    return (
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <section
          aria-labelledby="follow-up-message-heading"
          className={`${CARD_CLASS} gap-4 p-4 md:p-5`}
          style={{ boxShadow: "var(--follow-up-shadow)" }}
        >
          <div className="flex flex-col gap-1">
            <label
              id="follow-up-message-heading"
              htmlFor="follow-up-message"
              className="text-[length:var(--follow-up-text-fs)] font-[var(--follow-up-label-fw)] text-[var(--follow-up-title-fg)]"
            >
              Message
            </label>
            <textarea
              id="follow-up-message"
              ref={textareaRef}
              rows={12}
              value={message}
              maxLength={FOLLOW_UP_MESSAGE_MAX_LENGTH}
              onChange={(event) => setMessage(event.target.value)}
              className="w-full resize-y rounded-lg border border-[var(--follow-up-chip-border)] bg-[var(--follow-up-surface-bg)] p-3 text-[length:var(--follow-up-text-fs)] leading-relaxed text-[var(--follow-up-text-fg)] focus:outline-none focus:border-[var(--follow-up-accent)]"
            />
            <div className="flex flex-wrap items-center justify-between gap-2 text-[length:var(--follow-up-small-fs)]">
              <span className={trimmedMessage ? "text-[var(--follow-up-muted-fg)]" : "text-[var(--follow-up-error-fg)]"}>
                {trimmedMessage ? "Saved for every follow-up message" : "The message can't be empty"}
              </span>
              <span className="text-[var(--follow-up-muted-fg)]">
                {message.length}/{FOLLOW_UP_MESSAGE_MAX_LENGTH}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-[length:var(--follow-up-small-fs)] font-[var(--follow-up-label-fw)] uppercase tracking-wide text-[var(--follow-up-muted-fg)]">
              Insert a detail
            </p>
            <div className="flex flex-wrap gap-2">
              {MESSAGE_PLACEHOLDERS.map((placeholder) => (
                <button
                  key={placeholder.key}
                  type="button"
                  onClick={() => insertPlaceholder(placeholder.key)}
                  title={`Inserts {${placeholder.key}}`}
                  className="rounded-full bg-[var(--follow-up-placeholder-chip-bg)] px-3 py-1.5 text-[length:var(--follow-up-small-fs)] font-[var(--follow-up-label-fw)] text-[var(--follow-up-placeholder-chip-fg)] transition-colors hover:bg-[var(--follow-up-placeholder-chip-hover-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--follow-up-focus-ring)]"
                >
                  + {placeholder.label}
                </button>
              ))}
            </div>
            <p className="flex items-start gap-2 rounded-lg bg-[var(--follow-up-upcoming-bg)] p-3 text-[length:var(--follow-up-small-fs)] text-[var(--follow-up-upcoming-fg)]">
              <Info size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span>
                <strong>{"{items}"}</strong> lists the items ticked before sending, one per line
                with its quantity. Items due on different days also show their own date.
              </span>
            </p>
          </div>

          <div className="mt-auto flex flex-col-reverse gap-2 border-t border-[var(--follow-up-divider)] pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setMessage(DEFAULT_FOLLOW_UP_MESSAGE)}
              disabled={message === DEFAULT_FOLLOW_UP_MESSAGE}
              className={buttonClass("secondary")}
            >
              <RotateCcw size={16} aria-hidden="true" />
              Use default message
            </button>
            <button type="button" onClick={saveMessage} disabled={!canSave} className={buttonClass("primary")}>
              {isSaving ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <Save size={16} aria-hidden="true" />
              )}
              Save message
            </button>
          </div>
        </section>

        <MessagePreview message={fillTemplate(message, SAMPLE_MESSAGE_VALUES)} />
      </div>
    );
  };

  return (
    <InnerLayout title="Follow-up Message" backBtn={true} list={true}>
      <div className="h-full overflow-y-auto">
        <main className="flex flex-col gap-4 px-[24px] py-[16px]">
          <p className="text-[length:var(--follow-up-text-fs)] text-[var(--follow-up-muted-fg)]">
            The WhatsApp message the Customer Follow-up screen opens with. Details in braces
            are filled in for each customer.
          </p>
          {renderContent()}
        </main>
      </div>
    </InnerLayout>
  );
};

export default FollowUpMessageSettings;
